import { fitFontSize } from '../../utils/textFit';
import React, { useState, useEffect, useRef } from 'react';
import { 
  Move, Upload, Save, Eye, Trash2, Plus, Settings2, 
  Image as ImageIcon, Type, QrCode, FileText, RotateCcw,
  ZoomIn, ZoomOut, Users, X, Check, Circle, Square, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import api from '@/api/api';

// Standard I-Card Size (CR80): 85.6mm x 54mm
const CARD_WIDTH = 204;  // 54mm scaled
const CARD_HEIGHT = 324; // 86mm scaled

// Sample data for preview
const SAMPLE_STAFF = {
  photo: '',
  name: 'Dr. Amit Sharma',
  staff_type: 'Teaching',
  designation: 'Associate Professor',
  department: 'Computer Science',
  employee_id: 'EMP001',
  join_date: '2020-01-15',
  phone: '9876543210',
  blood_group: 'B+',
};

// Available fields for Staff I-Card
const AVAILABLE_FIELDS = {
  front: [
    { id: 'logo', label: 'College Logo', type: 'image', icon: ImageIcon },
    { id: 'college_name', label: 'College Name', type: 'text', icon: Type },
    { id: 'college_address', label: 'College Address', type: 'text', icon: Type },
    { id: 'photo', label: 'Staff Photo', type: 'image', icon: Users },
    { id: 'name', label: 'Staff Name', type: 'text', icon: Type },
    { id: 'staff_type', label: 'Staff Type', type: 'text', icon: Type },
    { id: 'designation', label: 'Designation', type: 'text', icon: Type },
    { id: 'department', label: 'Department', type: 'text', icon: Type },
    { id: 'employee_id', label: 'Employee ID', type: 'text', icon: Type },
    { id: 'join_date', label: 'Join Date', type: 'text', icon: Type },
    { id: 'phone', label: 'Phone', type: 'text', icon: Type },
    { id: 'blood_group', label: 'Blood Group', type: 'text', icon: Type },
    { id: 'authority_signature', label: 'Authority Signature', type: 'signature', icon: ImageIcon },
    { id: 'authority_name', label: 'Authority Name', type: 'text', icon: Type },
    { id: 'authority_designation', label: 'Authority Designation', type: 'text', icon: Type },
  ],
  back: [
    { id: 'terms_title', label: 'Terms Title', type: 'text', icon: Type },
    { id: 'terms', label: 'Terms & Conditions', type: 'textarea', icon: FileText },
    { id: 'employee_id_back', label: 'Employee ID', type: 'text', icon: Type },
    { id: 'qr_code', label: 'QR Code', type: 'qr', icon: QrCode },
    { id: 'college_contact', label: 'College Contact', type: 'text', icon: Type },
    { id: 'authority_signature_back', label: 'Authority Signature', type: 'signature', icon: ImageIcon },
    { id: 'authority_name_back', label: 'Authority Name', type: 'text', icon: Type },
  ]
};

// Default field configurations
const getDefaultFields = (side) => {
  if (side === 'front') {
    return [
      { id: 'logo', label: 'College Logo', type: 'image', x: 10, y: 10, width: 40, height: 40, visible: true, shape: 'square' },
      { id: 'college_name', label: 'College Name', type: 'text', x: 55, y: 15, width: 140, height: 20, visible: true, fontSize: 10, color: '#1e3a5f', fontWeight: 'bold', textAlign: 'center' },
      { id: 'college_address', label: 'College Address', type: 'text', x: 55, y: 35, width: 140, height: 15, visible: true, fontSize: 7, color: '#666', textAlign: 'center' },
      { id: 'photo', label: 'Staff Photo', type: 'image', x: 70, y: 60, width: 60, height: 75, visible: true, shape: 'square' },
      { id: 'name', label: 'Staff Name', type: 'text', x: 10, y: 145, width: 185, height: 18, visible: true, fontSize: 11, color: '#000', fontWeight: 'bold', textAlign: 'center' },
      { id: 'staff_type', label: 'Staff Type', type: 'text', x: 10, y: 165, width: 185, height: 14, visible: true, fontSize: 9, color: '#1e3a5f', fontWeight: 'bold', textAlign: 'center' },
      { id: 'designation', label: 'Designation', type: 'text', x: 10, y: 182, width: 185, height: 14, visible: true, fontSize: 8, color: '#333', textAlign: 'center' },
      { id: 'department', label: 'Department', type: 'text', x: 10, y: 198, width: 185, height: 14, visible: true, fontSize: 8, color: '#333', textAlign: 'center' },
      { id: 'employee_id', label: 'Employee ID', type: 'text', x: 10, y: 220, width: 90, height: 14, visible: true, fontSize: 8, color: '#000', textAlign: 'left' },
      { id: 'join_date', label: 'Join Date', type: 'text', x: 105, y: 220, width: 90, height: 14, visible: true, fontSize: 8, color: '#000', textAlign: 'left' },
      { id: 'phone', label: 'Phone', type: 'text', x: 10, y: 238, width: 90, height: 14, visible: true, fontSize: 8, color: '#000', textAlign: 'left' },
      { id: 'blood_group', label: 'Blood Group', type: 'text', x: 105, y: 238, width: 90, height: 14, visible: true, fontSize: 8, color: '#c00', textAlign: 'left' },
    ];
  } else {
    return [
      { id: 'terms_title', label: 'Terms Title', type: 'text', x: 10, y: 10, width: 185, height: 16, visible: true, fontSize: 10, color: '#1e3a5f', fontWeight: 'bold', textAlign: 'center' },
      { id: 'terms', label: 'Terms & Conditions', type: 'textarea', x: 10, y: 30, width: 185, height: 120, visible: true, fontSize: 7, color: '#333', textAlign: 'left' },
      { id: 'employee_id_back', label: 'Employee ID', type: 'text', x: 10, y: 160, width: 185, height: 14, visible: true, fontSize: 9, color: '#000', textAlign: 'center' },
      { id: 'qr_code', label: 'QR Code', type: 'qr', x: 70, y: 180, width: 60, height: 60, visible: true },
      { id: 'college_contact', label: 'College Contact', type: 'text', x: 10, y: 250, width: 185, height: 14, visible: true, fontSize: 7, color: '#666', textAlign: 'center' },
    ];
  }
};

// Draggable Field Component
const DraggableField = ({ field, onUpdate, onDelete, selected, onSelect, scale, template }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const fieldRef = useRef(null);

  const handleMouseDown = (e) => {
    if (e.target.classList.contains('resize-handle')) {
      setIsResizing(true);
    } else {
      setIsDragging(true);
    }
    setDragStart({ x: e.clientX, y: e.clientY });
    onSelect(field.id);
    e.preventDefault();
  };

  const handleMouseMove = (e) => {
    if (!isDragging && !isResizing) return;
    
    const dx = (e.clientX - dragStart.x) / scale;
    const dy = (e.clientY - dragStart.y) / scale;
    
    if (isDragging) {
      onUpdate(field.id, {
        x: Math.max(0, Math.min(field.x + dx, CARD_WIDTH - field.width)),
        y: Math.max(0, Math.min(field.y + dy, CARD_HEIGHT - field.height))
      });
    } else if (isResizing) {
      onUpdate(field.id, {
        width: Math.max(20, field.width + dx),
        height: Math.max(15, field.height + dy)
      });
    }
    
    setDragStart({ x: e.clientX, y: e.clientY });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    setIsResizing(false);
  };

  useEffect(() => {
    if (isDragging || isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, isResizing, dragStart]);

  if (!field.visible) return null;

  const isRound = field.shape === 'round';

  const getFieldContent = () => {
    const style = {
      fontSize: `${(field.fontSize || 10) * scale}px`,
      color: field.color || '#000',
      fontWeight: field.fontWeight || 'normal',
      textAlign: field.textAlign || 'left',
    };

    switch (field.id) {
      case 'logo':
        return template.college_logo ? (
          <img src={template.college_logo} className={`w-full h-full object-contain ${isRound ? 'rounded-full' : ''}`} alt="Logo" />
        ) : (
          <div className={`w-full h-full bg-gray-200 border-2 border-dashed border-gray-400 flex items-center justify-center ${isRound ? 'rounded-full' : ''}`}>
            <ImageIcon className="w-1/2 h-1/2 text-gray-400" />
          </div>
        );
      case 'photo':
        return (
          <div className={`w-full h-full bg-gray-200 border-2 border-dashed border-gray-400 flex items-center justify-center ${isRound ? 'rounded-full' : ''}`}>
            <Users className="w-1/2 h-1/2 text-gray-400" />
          </div>
        );
      case 'qr_code':
        return (
          <div className="w-full h-full bg-white border border-gray-300 flex items-center justify-center">
            <QrCode className="w-3/4 h-3/4 text-gray-600" />
          </div>
        );
      case 'terms':
        return (
          <div className="w-full h-full overflow-hidden text-left p-1 bg-gray-50 border border-gray-200" style={{ ...style, lineHeight: '1.4' }}>
            <div className="whitespace-pre-wrap">{template.terms_text || '1. This card is non-transferable.\n2. Must be worn during office hours.\n3. Report loss immediately.'}</div>
          </div>
        );
      case 'college_name':
        return <div className="w-full h-full flex items-center justify-center px-1" style={style}>{template.college_name || 'College Name'}</div>;
      case 'college_address':
        return <div className="w-full h-full flex items-center justify-center px-1" style={style}>{template.college_address || 'College Address'}</div>;
      case 'college_contact':
        const contact = [template.college_email, template.college_phone, template.college_website].filter(Boolean).join(' | ');
        return <div className="w-full h-full flex items-center justify-center px-1" style={style}>{contact || 'Email | Phone | Website'}</div>;
      case 'terms_title':
        return <div className="w-full h-full flex items-center justify-center px-1" style={style}>{template.terms_title || 'Terms & Conditions'}</div>;
      case 'name':
        return <div className="w-full h-full flex items-center justify-center px-1" style={style}>{SAMPLE_STAFF.name}</div>;
      case 'staff_type':
        return <div className="w-full h-full flex items-center justify-center px-1" style={style}>{SAMPLE_STAFF.staff_type}</div>;
      case 'designation':
        return <div className="w-full h-full flex items-center justify-center px-1" style={style}>{SAMPLE_STAFF.designation}</div>;
      case 'department':
        return <div className="w-full h-full flex items-center justify-center px-1" style={style}>{SAMPLE_STAFF.department}</div>;
      case 'employee_id':
        return <div className="w-full h-full flex items-center px-1" style={style}>ID: {SAMPLE_STAFF.employee_id}</div>;
      case 'employee_id_back':
        return <div className="w-full h-full flex items-center justify-center px-1" style={style}>Employee ID: {SAMPLE_STAFF.employee_id}</div>;
      case 'join_date':
        return <div className="w-full h-full flex items-center px-1" style={style}>Join: {SAMPLE_STAFF.join_date}</div>;
      case 'phone':
        return <div className="w-full h-full flex items-center px-1" style={style}>Ph: {SAMPLE_STAFF.phone}</div>;
      case 'blood_group':
        return <div className="w-full h-full flex items-center px-1" style={style}>Blood: {SAMPLE_STAFF.blood_group}</div>;
      case 'authority_signature':
      case 'authority_signature_back':
        return template.authority_signature ? (
          <img src={template.authority_signature} className="w-full h-full object-contain" alt="Signature" />
        ) : (
          <div className="w-full h-full bg-gray-100 border-2 border-dashed border-gray-400 flex items-center justify-center">
            <span className="text-xs text-gray-400">✍️ Signature</span>
          </div>
        );
      case 'authority_name':
      case 'authority_name_back':
        return <div className="w-full h-full flex items-center justify-center px-1" style={style}>{template.authority_name || 'Authority Name'}</div>;
      case 'authority_designation':
        return <div className="w-full h-full flex items-center justify-center px-1" style={style}>{template.authority_designation || 'Principal'}</div>;
      default:
        return <div className="w-full h-full flex items-center px-1 bg-blue-50/50 border border-blue-200/50" style={style}>{field.label}</div>;
    }
  };

  return (
    <div
      ref={fieldRef}
      className={`absolute cursor-move select-none ${selected ? 'ring-2 ring-blue-500 z-20' : 'z-10'}`}
      style={{
        left: `${field.x * scale}px`,
        top: `${field.y * scale}px`,
        width: `${field.width * scale}px`,
        height: `${field.height * scale}px`,
      }}
      onMouseDown={handleMouseDown}
      data-testid={`field-${field.id}`}
    >
      {getFieldContent()}
      
      {selected && (
        <>
          <button
            className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center text-xs hover:bg-red-600 z-30"
            onClick={(e) => { e.stopPropagation(); onDelete(field.id); }}
          >
            <X className="w-3 h-3" />
          </button>
          <div className="resize-handle absolute -bottom-1 -right-1 w-3 h-3 bg-blue-500 cursor-se-resize rounded-sm z-30" />
        </>
      )}
    </div>
  );
};

// Properties Panel Component
const PropertiesPanel = ({ field, onUpdate, allFields }) => {
  const fieldDef = allFields?.find(f => f.id === field?.id);
  
  if (!field) {
    return (
      <div className="p-4 text-gray-500 text-sm text-center">
        <Settings2 className="h-12 w-12 mx-auto mb-3 text-gray-300" />
        <p>Select a field to edit properties</p>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4">
      <div className="p-3 bg-blue-50 rounded-lg">
        <p className="font-medium text-blue-900">{fieldDef?.label || field.id}</p>
      </div>
      
      {field.type === 'image' && (
        <div>
          <label className="block text-xs text-gray-500 mb-1">Shape</label>
          <div className="flex gap-2">
            <button onClick={() => onUpdate(field.id, { shape: 'square' })} className={`flex-1 py-2 rounded flex items-center justify-center gap-1 ${field.shape !== 'round' ? 'bg-blue-600 text-white' : 'bg-gray-100'}`}>
              <Square className="h-4 w-4" /> Square
            </button>
            <button onClick={() => onUpdate(field.id, { shape: 'round' })} className={`flex-1 py-2 rounded flex items-center justify-center gap-1 ${field.shape === 'round' ? 'bg-blue-600 text-white' : 'bg-gray-100'}`}>
              <Circle className="h-4 w-4" /> Round
            </button>
          </div>
        </div>
      )}
      
      {(field.type === 'text' || field.type === 'textarea') && (
        <>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Font Size: {field.fontSize || 10}px</label>
            <input type="range" min="6" max="20" value={field.fontSize || 10} onChange={(e) => onUpdate(field.id, { fontSize: parseInt(e.target.value) })} className="w-full" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Color</label>
            <input type="color" value={field.color || '#000000'} onChange={(e) => onUpdate(field.id, { color: e.target.value })} className="w-full h-8 border rounded" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Font Weight</label>
            <select value={field.fontWeight || 'normal'} onChange={(e) => onUpdate(field.id, { fontWeight: e.target.value })} className="w-full px-2 py-1 border rounded text-sm">
              <option value="normal">Normal</option>
              <option value="bold">Bold</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Text Align</label>
            <select value={field.textAlign || 'left'} onChange={(e) => onUpdate(field.id, { textAlign: e.target.value })} className="w-full px-2 py-1 border rounded text-sm">
              <option value="left">Left</option>
              <option value="center">Center</option>
              <option value="right">Right</option>
            </select>
          </div>
        </>
      )}
      
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs text-gray-500 mb-1">X Position</label>
          <input type="number" value={Math.round(field.x)} onChange={(e) => onUpdate(field.id, { x: parseInt(e.target.value) || 0 })} className="w-full px-2 py-1 border rounded text-sm" />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Y Position</label>
          <input type="number" value={Math.round(field.y)} onChange={(e) => onUpdate(field.id, { y: parseInt(e.target.value) || 0 })} className="w-full px-2 py-1 border rounded text-sm" />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Width</label>
          <input type="number" value={Math.round(field.width)} onChange={(e) => onUpdate(field.id, { width: parseInt(e.target.value) || 50 })} className="w-full px-2 py-1 border rounded text-sm" />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Height</label>
          <input type="number" value={Math.round(field.height)} onChange={(e) => onUpdate(field.id, { height: parseInt(e.target.value) || 20 })} className="w-full px-2 py-1 border rounded text-sm" />
        </div>
      </div>
      
      <button onClick={() => onUpdate(field.id, { visible: false })} className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200">
        <Trash2 className="h-4 w-4" />
        Hide Field
      </button>
    </div>
  );
};

// Main Staff I-Card Builder Component
const StaffICardBuilder = () => {
  const [template, setTemplate] = useState({
    front_background: '',
    back_background: '',
    front_fields: getDefaultFields('front'),
    back_fields: getDefaultFields('back'),
    college_name: '',
    college_address: '',
    college_logo: '',
    college_email: '',
    college_website: '',
    college_phone: '',
    terms_title: 'Terms & Conditions',
    terms_text: '1. This card is non-transferable.\n2. Must be worn during office hours.\n3. Report loss immediately.\n4. Return on resignation.',
    authority_signature: '',
    authority_name: '',
    authority_designation: '',
  });
  
  const [activeSide, setActiveSide] = useState('front');
  const [selectedField, setSelectedField] = useState(null);
  const [scale, setScale] = useState(1.5);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showPreview, setShowPreview] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchTemplate();
  }, []);

  const fetchTemplate = async () => {
    setLoading(true);
    try {
      // Fetch saved template
      const response = await api.get('/staff-icard/template');
      if (response.data && response.data.front_fields && response.data.front_fields.length > 0) {
        setTemplate(prev => ({ ...prev, ...response.data }));
      }
      
      // Fetch college info to auto-fill
      await fetchCollegeInfo();
    } catch (error) {
      console.log('No existing template, using defaults');
    } finally {
      setLoading(false);
    }
  };

  const fetchCollegeInfo = async () => {
    try {
      const response = await api.get('/icard-builder/college-info');
      const info = response.data;
      if (info) {
        setTemplate(prev => ({
          ...prev,
          college_name: prev.college_name || info.college_name || '',
          college_address: prev.college_address || info.college_address || '',
          college_logo: prev.college_logo || info.college_logo || '',
          college_email: prev.college_email || info.college_email || '',
          college_website: prev.college_website || info.college_website || '',
          college_phone: prev.college_phone || info.college_phone || '',
        }));
      }
    } catch (error) {
      console.error('Error fetching college info:', error);
    }
  };

  const syncCollegeInfo = async () => {
    setSyncing(true);
    try {
      const response = await api.get('/icard-builder/college-info');
      const info = response.data;
      if (info) {
        setTemplate(prev => ({
          ...prev,
          college_name: info.college_name || prev.college_name,
          college_address: info.college_address || prev.college_address,
          college_logo: info.college_logo || prev.college_logo,
          college_email: info.college_email || prev.college_email,
          college_website: info.college_website || prev.college_website,
          college_phone: info.college_phone || prev.college_phone,
        }));
        toast.success('College info synced successfully!');
      }
    } catch (error) {
      toast.error('Failed to sync college info');
    } finally {
      setSyncing(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.post('/staff-icard/template', template);
      toast.success('Template saved successfully!');
    } catch (error) {
      console.error('Error saving template:', error);
      toast.error('Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const handleBackgroundUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onloadend = () => {
      const key = activeSide === 'front' ? 'front_background' : 'back_background';
      setTemplate(prev => ({ ...prev, [key]: reader.result }));
      toast.success('Background uploaded!');
    };
    reader.readAsDataURL(file);
  };

  const updateField = (fieldId, updates) => {
    const key = activeSide === 'front' ? 'front_fields' : 'back_fields';
    setTemplate(prev => ({
      ...prev,
      [key]: prev[key].map(f => f.id === fieldId ? { ...f, ...updates } : f)
    }));
  };

  const deleteField = (fieldId) => {
    updateField(fieldId, { visible: false });
    setSelectedField(null);
    toast.success('Field hidden');
  };

  const addField = (fieldDef) => {
    const key = activeSide === 'front' ? 'front_fields' : 'back_fields';
    const existingField = template[key].find(f => f.id === fieldDef.id);
    
    if (existingField) {
      updateField(fieldDef.id, { visible: true });
      toast.success(`${fieldDef.label} shown`);
    } else {
      const newField = {
        id: fieldDef.id,
        label: fieldDef.label,
        type: fieldDef.type,
        x: 50,
        y: 50,
        width: fieldDef.type === 'image' ? 60 : fieldDef.type === 'signature' ? 50 : fieldDef.type === 'qr' ? 60 : fieldDef.type === 'textarea' ? 185 : 100,
        height: fieldDef.type === 'image' ? 60 : fieldDef.type === 'signature' ? 25 : fieldDef.type === 'qr' ? 60 : fieldDef.type === 'textarea' ? 120 : 18,
        visible: true,
        fontSize: 10,
        color: '#000000',
        fontWeight: 'normal',
        textAlign: ['authority_name', 'authority_designation', 'authority_name_back'].includes(fieldDef.id) ? 'center' : 'left',
        shape: 'square',
      };
      setTemplate(prev => ({
        ...prev,
        [key]: [...prev[key], newField]
      }));
      toast.success(`${fieldDef.label} added`);
    }
  };

  const handleSignatureUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    if (file.size > 100 * 1024) {
      toast.error('Signature image must be less than 100KB');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setTemplate(prev => ({
        ...prev,
        authority_signature: reader.result
      }));
      toast.success('Signature uploaded!');
    };
    reader.onerror = () => {
      toast.error('Failed to read signature file');
    };
    reader.readAsDataURL(file);
  };

  const resetToDefault = () => {
    const key = activeSide === 'front' ? 'front_fields' : 'back_fields';
    setTemplate(prev => ({
      ...prev,
      [key]: getDefaultFields(activeSide)
    }));
    setSelectedField(null);
    toast.success('Reset to default layout');
  };

  const currentFields = activeSide === 'front' ? template.front_fields : template.back_fields;
  const currentBackground = activeSide === 'front' ? template.front_background : template.back_background;
  const availableFields = AVAILABLE_FIELDS[activeSide];
  const selectedFieldData = currentFields.find(f => f.id === selectedField);

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-gray-100">
      {/* Header */}
      <div className="bg-white border-b px-6 py-3 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Staff I-Card Builder</h1>
          <p className="text-sm text-gray-500">Design Staff I-Card template by dragging fields</p>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={syncCollegeInfo} disabled={syncing} className="flex items-center gap-2 px-3 py-2 bg-gray-100 rounded-lg hover:bg-gray-200">
            <RefreshCw className={`h-4 w-4 ${syncing ? 'animate-spin' : ''}`} />
            Sync College Info
          </button>
          <button onClick={() => setShowPreview(true)} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
            <Eye className="h-4 w-4" />
            Preview
          </button>
          <button onClick={handleSave} disabled={saving} className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50">
            <Save className="h-4 w-4" />
            {saving ? 'Saving...' : 'Save Template'}
          </button>
          <Link to="/hr/print-staff-icard" className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700">
            Print I-Cards
          </Link>
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* Left Panel - Available Fields */}
        <div className="w-64 bg-white border-r p-4 overflow-y-auto">
          <h3 className="font-semibold text-gray-900 mb-3">Available Fields</h3>
          
          {/* Side Toggle */}
          <div className="flex gap-2 mb-4">
            <button
              onClick={() => { setActiveSide('front'); setSelectedField(null); }}
              className={`flex-1 py-2 text-sm rounded-lg ${activeSide === 'front' ? 'bg-blue-600 text-white' : 'bg-gray-100'}`}
            >
              Front Side
            </button>
            <button
              onClick={() => { setActiveSide('back'); setSelectedField(null); }}
              className={`flex-1 py-2 text-sm rounded-lg ${activeSide === 'back' ? 'bg-blue-600 text-white' : 'bg-gray-100'}`}
            >
              Back Side
            </button>
          </div>
          
          {/* Field List */}
          <div className="space-y-2">
            {availableFields.map(field => {
              const isActive = currentFields.find(f => f.id === field.id && f.visible);
              return (
                <button
                  key={field.id}
                  onClick={() => addField(field)}
                  className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left text-sm transition-colors ${
                    isActive ? 'bg-green-100 text-green-700' : 'bg-gray-50 hover:bg-gray-100'
                  }`}
                >
                  <field.icon className="h-4 w-4" />
                  {field.label}
                  {isActive && <Check className="h-3 w-3 ml-auto" />}
                </button>
              );
            })}
          </div>
          
          {/* Background Upload */}
          <div className="mt-6 pt-4 border-t">
            <h4 className="font-medium text-gray-700 mb-2">Background Image</h4>
            <input type="file" ref={fileInputRef} onChange={handleBackgroundUpload} accept="image/*" className="hidden" />
            <button onClick={() => fileInputRef.current?.click()} className="w-full flex items-center justify-center gap-2 px-3 py-2 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50">
              <Upload className="h-4 w-4" />
              Upload Background
            </button>
            {currentBackground && (
              <button onClick={() => setTemplate(prev => ({ ...prev, [activeSide === 'front' ? 'front_background' : 'back_background']: '' }))} className="w-full mt-2 text-sm text-red-600 hover:text-red-700">
                Remove Background
              </button>
            )}
          </div>
          
          {/* Reset Button */}
          <button onClick={resetToDefault} className="w-full mt-4 flex items-center justify-center gap-2 px-3 py-2 bg-gray-100 rounded-lg hover:bg-gray-200 text-sm text-gray-700">
            <RotateCcw className="h-4 w-4" />
            Reset to Default
          </button>
        </div>

        {/* Center - Canvas */}
        <div className="flex-1 flex items-center justify-center p-8 overflow-auto bg-gray-200" onClick={() => setSelectedField(null)}>
          <div
            ref={canvasRef}
            className="relative bg-white shadow-2xl"
            style={{
              width: `${CARD_WIDTH * scale}px`,
              height: `${CARD_HEIGHT * scale}px`,
              backgroundImage: currentBackground ? `url(${currentBackground})` : 'none',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Grid overlay when no background */}
            {!currentBackground && (
              <div 
                className="absolute inset-0 opacity-10"
                style={{
                  backgroundImage: 'linear-gradient(#000 1px, transparent 1px), linear-gradient(90deg, #000 1px, transparent 1px)',
                  backgroundSize: `${10 * scale}px ${10 * scale}px`
                }}
              />
            )}
            
            {/* Render Draggable Fields */}
            {currentFields.map(field => (
              <DraggableField
                key={field.id}
                field={field}
                onUpdate={updateField}
                onDelete={deleteField}
                selected={selectedField === field.id}
                onSelect={setSelectedField}
                scale={scale}
                template={template}
              />
            ))}
          </div>
        </div>

        {/* Right Panel - Properties */}
        <div className="w-72 bg-white border-l overflow-y-auto">
          <div className="p-4 border-b">
            <h3 className="font-semibold text-gray-900">Properties</h3>
          </div>
          
          <PropertiesPanel 
            field={selectedFieldData} 
            onUpdate={updateField}
            allFields={availableFields}
          />
          
          {/* Zoom Controls */}
          <div className="p-4 border-t">
            <label className="block text-xs text-gray-500 mb-2">Zoom: {Math.round(scale * 100)}%</label>
            <div className="flex items-center gap-2">
              <button onClick={() => setScale(Math.max(0.5, scale - 0.25))} className="p-2 bg-gray-100 rounded hover:bg-gray-200">
                <ZoomOut className="h-4 w-4" />
              </button>
              <input type="range" min="0.5" max="2" step="0.25" value={scale} onChange={(e) => setScale(parseFloat(e.target.value))} className="flex-1" />
              <button onClick={() => setScale(Math.min(2, scale + 0.25))} className="p-2 bg-gray-100 rounded hover:bg-gray-200">
                <ZoomIn className="h-4 w-4" />
              </button>
            </div>
          </div>
          
          {/* Terms Settings */}
          <div className="p-4 border-t">
            <h4 className="font-medium text-gray-700 mb-3">Terms & Conditions</h4>
            <div className="space-y-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Title</label>
                <input type="text" value={template.terms_title} onChange={(e) => setTemplate(prev => ({ ...prev, terms_title: e.target.value }))} className="w-full px-2 py-1 border rounded text-sm" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Text</label>
                <textarea value={template.terms_text} onChange={(e) => setTemplate(prev => ({ ...prev, terms_text: e.target.value }))} rows={4} className="w-full px-2 py-1 border rounded text-sm" />
              </div>
            </div>
          </div>
          
          {/* College Info */}
          <div className="p-4 border-t">
            <h4 className="font-medium text-gray-700 mb-3">College Information</h4>
            <div className="space-y-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1">College Name</label>
                <input type="text" value={template.college_name} onChange={(e) => setTemplate(prev => ({ ...prev, college_name: e.target.value }))} className="w-full px-2 py-1 border rounded text-sm" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Address</label>
                <input type="text" value={template.college_address} onChange={(e) => setTemplate(prev => ({ ...prev, college_address: e.target.value }))} className="w-full px-2 py-1 border rounded text-sm" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Email</label>
                <input type="email" value={template.college_email} onChange={(e) => setTemplate(prev => ({ ...prev, college_email: e.target.value }))} className="w-full px-2 py-1 border rounded text-sm" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Phone</label>
                <input type="text" value={template.college_phone} onChange={(e) => setTemplate(prev => ({ ...prev, college_phone: e.target.value }))} className="w-full px-2 py-1 border rounded text-sm" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Website</label>
                <input type="text" value={template.college_website} onChange={(e) => setTemplate(prev => ({ ...prev, college_website: e.target.value }))} className="w-full px-2 py-1 border rounded text-sm" />
              </div>
            </div>
            
            {/* Authority Signature Section */}
            <div className="mt-4 pt-4 border-t">
              <h4 className="text-sm font-semibold text-gray-700 mb-3">✍️ Authority Signature</h4>
              
              <div className="mb-3">
                <label className="block text-xs text-gray-500 mb-1">Signature Image (max 100KB)</label>
                <label className="block">
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-2 text-center cursor-pointer hover:border-blue-400 transition-colors">
                    {template.authority_signature ? (
                      <div className="flex items-center gap-2">
                        <img src={template.authority_signature} alt="" className="h-8 object-contain" />
                        <span className="text-xs text-gray-500">Click to change</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 justify-center">
                        <Upload className="h-4 w-4 text-gray-400" />
                        <span className="text-xs text-gray-500">Upload Signature</span>
                      </div>
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleSignatureUpload}
                      className="hidden"
                    />
                  </div>
                </label>
              </div>
              
              <div className="space-y-2">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Authority Name</label>
                  <input
                    type="text"
                    value={template.authority_name}
                    onChange={(e) => setTemplate(prev => ({ ...prev, authority_name: e.target.value }))}
                    className="w-full px-2 py-1 border rounded text-sm"
                    placeholder="Dr. John Smith"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Designation</label>
                  <input
                    type="text"
                    value={template.authority_designation}
                    onChange={(e) => setTemplate(prev => ({ ...prev, authority_designation: e.target.value }))}
                    className="w-full px-2 py-1 border rounded text-sm"
                    placeholder="Principal"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-8" onClick={() => setShowPreview(false)}>
          <div className="bg-white rounded-xl p-6 max-w-4xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">I-Card Preview</h3>
              <button onClick={() => setShowPreview(false)} className="p-2 hover:bg-gray-100 rounded-full">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="flex gap-8 justify-center">
              {/* Front Preview */}
              <div>
                <p className="text-center text-sm text-gray-500 mb-2">Front Side</p>
                <div className="relative bg-white shadow-lg border" style={{ width: `${CARD_WIDTH * 1.5}px`, height: `${CARD_HEIGHT * 1.5}px`, backgroundImage: template.front_background ? `url(${template.front_background})` : 'none', backgroundSize: 'cover' }}>
                  {template.front_fields.filter(f => f.visible).map(field => (
                    <div key={field.id} className={`absolute ${['photo','logo','qr_code','qr','signature'].includes(field.id) ? 'overflow-hidden' : ''}`} style={{ left: `${field.x * 1.5}px`, top: `${field.y * 1.5}px`, width: `${field.width * 1.5}px`, height: ['photo','logo','qr_code','qr','signature'].includes(field.id) ? `${field.height * 1.5}px` : 'auto', minHeight: `${field.height * 1.5}px`, fontSize: field.id === 'college_name' ? `${fitFontSize(template?.college_name, (field.fontSize || 10), field.width) * 1.5}px` : `${(field.fontSize || 10) * 1.5}px`, color: field.color, fontWeight: field.fontWeight, textAlign: field.textAlign, display: 'flex', alignItems: 'center', justifyContent: field.textAlign === 'center' ? 'center' : 'flex-start', lineHeight: 1.15, borderRadius: field.shape === 'round' ? '50%' : '0' }}>
                      {field.id === 'logo' && (template.college_logo ? <img src={template.college_logo} className="w-full h-full object-contain" alt="" /> : <ImageIcon className="w-1/2 h-1/2 text-gray-400 mx-auto" />)}
                      {field.id === 'photo' && <Users className="w-1/2 h-1/2 text-gray-400 mx-auto bg-gray-100 rounded" />}
                      {field.id === 'college_name' && (template.college_name || 'College Name')}
                      {field.id === 'college_address' && (template.college_address || 'College Address')}
                      {field.id === 'name' && SAMPLE_STAFF.name}
                      {field.id === 'staff_type' && SAMPLE_STAFF.staff_type}
                      {field.id === 'designation' && SAMPLE_STAFF.designation}
                      {field.id === 'department' && SAMPLE_STAFF.department}
                      {field.id === 'employee_id' && `ID: ${SAMPLE_STAFF.employee_id}`}
                      {field.id === 'join_date' && `Join: ${SAMPLE_STAFF.join_date}`}
                      {field.id === 'phone' && `Ph: ${SAMPLE_STAFF.phone}`}
                      {field.id === 'blood_group' && `Blood: ${SAMPLE_STAFF.blood_group}`}
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Back Preview */}
              <div>
                <p className="text-center text-sm text-gray-500 mb-2">Back Side</p>
                <div className="relative bg-white shadow-lg border" style={{ width: `${CARD_WIDTH * 1.5}px`, height: `${CARD_HEIGHT * 1.5}px`, backgroundImage: template.back_background ? `url(${template.back_background})` : 'none', backgroundSize: 'cover' }}>
                  {template.back_fields.filter(f => f.visible).map(field => (
                    <div key={field.id} className={`absolute ${field.type === 'textarea' ? 'overflow-hidden' : ''}`} style={{ left: `${field.x * 1.5}px`, top: `${field.y * 1.5}px`, width: `${field.width * 1.5}px`, height: field.type === 'textarea' ? `${field.height * 1.5}px` : 'auto', minHeight: `${field.height * 1.5}px`, fontSize: `${(field.fontSize || 10) * 1.5}px`, color: field.color, fontWeight: field.fontWeight, textAlign: field.textAlign, display: 'flex', alignItems: field.type === 'textarea' ? 'flex-start' : 'center', justifyContent: field.textAlign === 'center' ? 'center' : 'flex-start', lineHeight: field.type === 'textarea' ? '1.4' : 1.15 }}>
                      {field.id === 'terms_title' && (template.terms_title || 'Terms & Conditions')}
                      {field.id === 'terms' && <div className="whitespace-pre-wrap text-left w-full">{template.terms_text}</div>}
                      {field.id === 'employee_id_back' && `Employee ID: ${SAMPLE_STAFF.employee_id}`}
                      {field.id === 'qr_code' && <QrCode className="w-full h-full text-gray-600" />}
                      {field.id === 'college_contact' && ([template.college_email, template.college_phone].filter(Boolean).join(' | ') || 'Email | Phone')}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StaffICardBuilder;
